import { useState } from 'react'
import type { components } from '../../../api/generated/schema'
import {
  Button,
  Card,
  ConsequenceSlot,
  Input,
  SectionLabel,
  StressTrack,
  Textarea,
} from '../../../design/components'
import { consequenceLevelName } from '../types'
import type { CharacterEditing } from '../useCharacterEditing'
import { SHEET_SECTION_ID } from './sheetSections'
import styles from './sections.module.css'

type StressSection = components['schemas']['StressSectionDto']
type ConsequencesSection = components['schemas']['ConsequencesSectionDto']
type Slot = components['schemas']['ConsequenceSlotDto']

/**
 * Stress tracks and consequence slots, in one blood-spined card (spec §5.1).
 *
 * Marking a box is optimistic: it is one bit with an obvious inverse, and at the
 * table the box must fill the instant it is tapped. Filling a consequence is not
 * — it can cascade, so it shows a pending state and waits for the server.
 */
export function HarmCard({
  stress,
  consequences,
  editing,
}: {
  stress: StressSection | undefined
  consequences: ConsequencesSection | undefined
  editing?: CharacterEditing | undefined
}) {
  if (stress === undefined && consequences === undefined) return null

  const canEdit = editing?.canEdit === true
  const tracks = stress?.tracks ?? []
  const slots = consequences?.slots ?? []
  const isHunger = (track: (typeof tracks)[number]) => String(track.type) === 'Hunger'
  const anyMarked = tracks.some(
    (track) => !isHunger(track) && (track.boxes ?? []).some((box) => box.isStressed),
  )
  // Feeding is offered whenever there is a Hunger track, marked or not: a vampire who has
  // just fed is a thing the table says out loud, and a button that vanishes when the track
  // is empty reads as a bug.
  const hasHunger = tracks.some(isHunger)
  const hungerMarked = tracks.some(
    (track) => isHunger(track) && (track.boxes ?? []).some((box) => box.isStressed),
  )

  return (
    <Card
      id={SHEET_SECTION_ID.harm}
      variant="harm"
      label="Stress &amp; consequences"
      hint={canEdit ? 'Tap to mark' : undefined}
    >
      <div className={styles.tracks}>
        {tracks.length === 0 ? (
          <p className={styles.empty}>No stress tracks.</p>
        ) : (
          tracks.map((track) => (
            <StressTrack
              key={String(track.type)}
              name={String(track.type ?? '')}
              boxes={(track.boxes ?? []).map((box) => ({
                id: box.id ?? 0,
                value: box.value ?? 0,
                isStressed: box.isStressed ?? false,
              }))}
              {...(canEdit && editing !== undefined
                ? {
                    onToggle: (slotId: number, next: boolean) =>
                      editing.stress.mutate({ slotId, isStressed: next }),
                  }
                : {})}
            />
          ))
        )}
      </div>

      {canEdit && editing !== undefined && (anyMarked || hasHunger) && (
        <div className={styles.actions}>
          {anyMarked && (
            <Button
              variant="secondary"
              disabled={editing.clearStress.isPending}
              onClick={() => editing.clearStress.mutate()}
            >
              Clear all stress
            </Button>
          )}
          {hasHunger && (
            <Button
              variant="secondary"
              disabled={editing.feed.isPending || !hungerMarked}
              onClick={() => editing.feed.mutate()}
            >
              Feed
            </Button>
          )}
        </div>
      )}

      {slots.length > 0 && (
        <>
          <hr className={styles.divider} />
          <SectionLabel tone="blood" className={styles.subhead}>
            Consequences
          </SectionLabel>
          <div className={styles.stack}>
            {slots.map((slot) => (
              <ConsequenceRow
                key={slot.id}
                slot={slot}
                {...(canEdit && editing !== undefined ? { editing } : {})}
              />
            ))}
          </div>
        </>
      )}
    </Card>
  )
}

function ConsequenceRow({ slot, editing }: { slot: Slot; editing?: CharacterEditing }) {
  const [open, setOpen] = useState(false)
  const filled = slot.isOccupied === true && slot.consequence?.name != null

  if (editing === undefined) {
    return (
      <ConsequenceSlot
        level={consequenceLevelName(slot.level as string | undefined)}
        label={String(slot.level ?? '')}
        {...(filled ? { text: consequenceText(slot) } : {})}
      />
    )
  }

  if (open) {
    return <ConsequenceForm slot={slot} editing={editing} onClose={() => setOpen(false)} />
  }

  return (
    <ConsequenceSlot
      level={consequenceLevelName(slot.level as string | undefined)}
      label={String(slot.level ?? '')}
      onClick={() => setOpen(true)}
      disabled={editing.isPending}
      {...(filled ? { text: consequenceText(slot) } : {})}
    />
  )
}

function ConsequenceForm({
  slot,
  editing,
  onClose,
}: {
  slot: Slot
  editing: CharacterEditing
  onClose: () => void
}) {
  const [name, setName] = useState(slot.consequence?.name ?? '')
  const [effect, setEffect] = useState(slot.consequence?.effect ?? '')
  const filled = slot.isOccupied === true

  const pending = editing.setConsequence.isPending || editing.clearConsequence.isPending

  return (
    <form
      className={styles.inlineForm}
      onSubmit={(event) => {
        event.preventDefault()
        editing.setConsequence.mutate(
          { slotId: slot.id ?? 0, name, effect: effect === '' ? null : effect },
          { onSuccess: onClose },
        )
      }}
    >
      <SectionLabel tone="blood" micro>
        {String(slot.level ?? '')} consequence
      </SectionLabel>

      <Input
        label="Name"
        value={name}
        autoFocus
        onChange={(event) => setName(event.target.value)}
        hint="The name becomes an invokable aspect against its owner."
      />
      <Textarea label="Effect" value={effect} onChange={(event) => setEffect(event.target.value)} />

      <div className={styles.actions}>
        <Button variant="secondary" onClick={onClose} disabled={pending}>
          Cancel
        </Button>
        {filled && (
          <Button
            variant="danger"
            disabled={pending}
            onClick={() =>
              editing.clearConsequence.mutate({ slotId: slot.id ?? 0 }, { onSuccess: onClose })
            }
          >
            Clear
          </Button>
        )}
        <Button type="submit" disabled={pending || name.trim() === ''}>
          {pending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </form>
  )
}

/**
 * A filled consequence reads as its name; the effect follows when the two differ.
 * The name itself becomes an invokable aspect against its owner, so it leads.
 */
function consequenceText(slot: Slot): string {
  const name = slot.consequence?.name ?? ''
  const effect = slot.consequence?.effect ?? ''
  if (effect === '' || effect === name) return name
  return `${name} — ${effect}`
}
